import { useEffect, useState } from 'react'
import { useAppKitState, useAppKitNetwork } from '@reown/appkit/react'
import { useSwitchChain } from 'wagmi'
import { ChainId } from '@nominex/nomiswap-sdk'
import { CHAIN_MAP, CHAINS, useWagmi } from 'libs/wagmi'
import useUrlSearchValues from './useUrlSearchValues'

const BNBChain = CHAINS.find(({ id }) => id === ChainId.BSC)

// Helper to parse CAIP chain ID (e.g., "eip155:56" -> 56)
const parseChainId = (caipChainId: number | string | undefined): number | undefined => {
  if (!caipChainId) return undefined

  // If it's already a number, return it
  if (typeof caipChainId === 'number') return caipChainId

  // Parse CAIP format: "eip155:56" -> 56
  const match = caipChainId.toString().match(/eip155:(\d+)/)
  if (match) {
    return parseInt(match[1], 10)
  }

  // Try to parse as number string
  const parsed = parseInt(caipChainId.toString(), 10)
  return Number.isNaN(parsed) ? undefined : parsed
}

// Helper to get chain name from chain ID
const getChainNameFromId = (chainId: number | string | undefined): string => {
  const parsedId = parseChainId(chainId)
  if (!parsedId) return 'unknown'

  // Find chain name from CHAIN_MAP
  const entry = Object.entries(CHAIN_MAP).find(([, id]) => id === parsedId)
  return entry ? entry[0] : 'unknown'
}

export default function useNetworkSwitching() {
  const { chain, isConnected } = useWagmi()
  const { selectedNetworkId } = useAppKitState()
  const { switchNetwork } = useAppKitNetwork()
  const { switchChainAsync, isPending } = useSwitchChain()
  const [chainNameFromUrl, setChainNameToUrl] = useUrlSearchValues('chain')

  const [chainFromWallet] = useState(CHAINS.find(({ id }) => id === chain?.id))
  const [chainFromUrl] = useState(CHAINS.find(({ id }) => id === Number(CHAIN_MAP[chainNameFromUrl])))
  const [initialChain] = useState(chainFromUrl || chainFromWallet || BNBChain)

  const [hasToSwitchNetwork, setHasToSwitchNetwork] = useState(isConnected ? chainFromWallet !== initialChain : true)

  const currentSelectedChain = CHAINS.find(({ id }) => id === parseChainId(selectedNetworkId))

  useEffect(() => {
    if (!isConnected && parseChainId(selectedNetworkId) === initialChain.id) {
      setHasToSwitchNetwork(false)
    }
  }, [initialChain.id, isConnected, selectedNetworkId])

  useEffect(() => {
    if (!isConnected && hasToSwitchNetwork && currentSelectedChain && currentSelectedChain.id !== initialChain.id) {
      switchNetwork(initialChain)
      setHasToSwitchNetwork(false)
    }
  }, [hasToSwitchNetwork, initialChain, isConnected, currentSelectedChain, switchNetwork])

  useEffect(() => {
    if (
      isConnected &&
      hasToSwitchNetwork &&
      !isPending &&
      switchChainAsync &&
      currentSelectedChain &&
      currentSelectedChain.id !== initialChain.id
    ) {
      switchChainAsync({ chainId: initialChain.id }).finally(() => {
        setHasToSwitchNetwork(false)
      })
    }
  }, [hasToSwitchNetwork, initialChain.id, isConnected, isPending, currentSelectedChain, switchChainAsync])

  useEffect(() => {
    if (selectedNetworkId) {
      const parsedSelectedId = parseChainId(selectedNetworkId)
      const chainIdFromUrl = Number(CHAIN_MAP[chainNameFromUrl])

      if (parsedSelectedId && parsedSelectedId !== chainIdFromUrl) {
        const chainName = getChainNameFromId(selectedNetworkId)
        setChainNameToUrl(chainName)
      }
    }
  })
}
